import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useWeb3 } from './Web3Context';

const WebSocketContext = createContext(null);

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function WebSocketProvider({ children }) {
    const { address } = useWeb3();
    const [isConnected, setIsConnected] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);
    const [liveFeed, setLiveFeed] = useState([]);
    const [statsUpdate, setStatsUpdate] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);

    const connect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) return;

        try {
            const ws = new WebSocket(WS_URL);
            
            ws.onopen = () => {
                console.log('WebSocket connected');
                setIsConnected(true);
                reconnectAttemptsRef.current = 0;
                
                // Authenticate with wallet address if connected
                if (address) {
                    ws.send(JSON.stringify({ type: 'auth', address }));
                }
            };
            
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    setLastMessage(data);
                    
                    // Handle different message types
                    if (data.type === 'live_feed') {
                        setLiveFeed(prev => [data.data, ...prev].slice(0, 20));
                    } else if (data.type === 'notification') {
                        // Notification is handled by NotificationCenter
                    } else if (data.type === 'stats_update') {
                        setStatsUpdate(data.data);
                    }
                } catch (e) {
                    console.error('WebSocket message parse error:', e);
                }
            };
            
            ws.onclose = () => {
                console.log('WebSocket disconnected');
                setIsConnected(false);
                wsRef.current = null;
                
                // Reconnect with exponential backoff
                if (reconnectAttemptsRef.current < 5) {
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptsRef.current++;
                        connect();
                    }, delay);
                }
            };
            
            ws.onerror = (error) => {
                console.log('WebSocket error, using polling fallback');
            };
            
            wsRef.current = ws;
        } catch (error) {
            console.error('WebSocket connection error:', error);
            // Fall back to polling
            setIsConnected(true);
        }
    }, [address]);

    const disconnect = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        setIsConnected(false);
    }, []);

    // Auto-connect on mount
    useEffect(() => {
        connect();
        return () => disconnect();
    }, [connect, disconnect]);

    // Re-authenticate when address changes
    useEffect(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN && address) {
            wsRef.current.send(JSON.stringify({ type: 'auth', address }));
        }
    }, [address]);

    // Polling fallback for live feed and stats
    useEffect(() => {
        const pollInterval = setInterval(async () => {
            try {
                // Fetch live feed
                const feedResponse = await fetch(`${API_URL}/api/live/recent?limit=10`);
                if (feedResponse.ok) {
                    const feedData = await feedResponse.json();
                    setLiveFeed(feedData.map(item => ({
                        bet_id: item.bet_id,
                        description: item.description,
                        amount: item.amount,
                        type: item.type,
                        timestamp: item.timestamp
                    })));
                }
                
                // Fetch stats
                const statsResponse = await fetch(`${API_URL}/api/stats`);
                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    setStatsUpdate(statsData);
                }
            } catch (error) {
                // Silent fail for polling
            }
        }, 10000); // Poll every 10 seconds

        // Initial fetch
        (async () => {
            try {
                const response = await fetch(`${API_URL}/api/live/recent?limit=10`);
                if (response.ok) {
                    const data = await response.json();
                    setLiveFeed(data);
                }
            } catch (e) {}
        })();

        return () => clearInterval(pollInterval);
    }, []);

    const sendMessage = useCallback((message) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(message));
        }
    }, []);

    const value = {
        isConnected,
        lastMessage,
        liveFeed,
        statsUpdate,
        sendMessage,
        connect,
        disconnect,
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
}

export function useWebSocket() {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
}
